/**
 * Same-origin browser-auth coordination channel.
 */
export const AUTH_CHANNEL_NAME = 'gimfront-auth';
/** Delay before resolving cross-tab refresh claim ownership. */
export const AUTH_REFRESH_CLAIM_DELAY_MS = 50;

let authChannel = null;
const authSubscribers = new Set();
const refreshClaims = new Map();
const authTabId =
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;

const handleMessage = (message) => {
  const event = message?.data;
  if (!event || typeof event !== 'object') return;
  if (
    event.type === 'refresh-claim' &&
    typeof event.tabId === 'string' &&
    typeof event.sessionEpoch === 'number' &&
    typeof event.issuedAt === 'number'
  ) {
    refreshClaims.set(event.tabId, event);
  }
  authSubscribers.forEach((subscriber) => subscriber(event));
};

/**
 * Returns shared BroadcastChannel when browser supports it.
 * @returns {BroadcastChannel|null}
 */
const getAuthChannel = () => {
  if (authChannel) return authChannel;
  if (typeof BroadcastChannel === 'undefined') return null;
  authChannel = new BroadcastChannel(AUTH_CHANNEL_NAME);
  authChannel.addEventListener('message', handleMessage);
  return authChannel;
};

/**
 * Publishes an in-memory authentication event to sibling tabs.
 * @param {Record<string, unknown>} event event payload
 * @returns {void}
 */
export const publishAuthEvent = (event) => {
  getAuthChannel()?.postMessage(event);
};

/**
 * Subscribes to sibling-tab authentication events.
 * @param {(event: Record<string, unknown>) => void} onEvent event callback
 * @returns {() => void} unsubscribe callback
 */
export const subscribeAuthEvents = (onEvent) => {
  const channel = getAuthChannel();
  if (!channel) return () => {};

  authSubscribers.add(onEvent);
  return () => authSubscribers.delete(onEvent);
};

/**
 * Coordinates a refresh claim with sibling tabs.
 * @param {number} sessionEpoch local authentication session epoch
 * @returns {Promise<boolean>} whether this tab owns the refresh attempt
 */
export const requestRefreshClaim = (sessionEpoch) => {
  const channel = getAuthChannel();
  if (!channel) return Promise.resolve(true);

  const claim = {
    type: 'refresh-claim',
    tabId: authTabId,
    sessionEpoch,
    issuedAt: Date.now(),
  };
  refreshClaims.set(authTabId, claim);
  try {
    channel.postMessage(claim);
  } catch {
    refreshClaims.delete(authTabId);
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    setTimeout(() => {
      const contenders = [...refreshClaims.values()].filter(
        (item) =>
          item.sessionEpoch === sessionEpoch &&
          Date.now() - item.issuedAt < 1000
      );
      contenders.sort(
        (left, right) =>
          left.issuedAt - right.issuedAt ||
          left.tabId.localeCompare(right.tabId)
      );
      refreshClaims.delete(authTabId);
      resolve(contenders[0]?.tabId === authTabId);
    }, AUTH_REFRESH_CLAIM_DELAY_MS);
  });
};
